import { obligationOwnership } from "../../lib/obligationOwnership";
import { ObligationOwnership } from "./ObligationOwnership";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import type { OperationalObligation } from "@io-spoke/contracts/obligations";
import { assigneeTypeSchema, type AssigneeType } from "@io-spoke/contracts/assignments";
import { toast } from "sonner";
import { applyObligationAction } from "../../lib/obligations";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AccountableOwnerField, type OwnerSuggestion } from "./AccountableOwnerField";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Textarea } from "../ui/textarea";

type Props = {
  obligation: OperationalObligation;
  ownerSuggestions?: OwnerSuggestion[];
  onUpdated: () => void | Promise<void>;
};

const assignFormSchema = z.object({
  ownerType: assigneeTypeSchema,
  ownerId: z.string().trim().min(1, "Enter the team, role, person, or agent responsible for this obligation.").max(200),
});
type AssignFormValues = z.infer<typeof assignFormSchema>;

const dismissFormSchema = z.object({
  reason: z.string().trim().min(10, "Explain why this is not an obligation in at least 10 characters.").max(500),
});
type DismissFormValues = z.infer<typeof dismissFormSchema>;

function assignmentDefaults(obligation: OperationalObligation): AssignFormValues {
  if (!obligationOwnership(obligation).recorded) return { ownerType: "team", ownerId: "" };
  return { ownerType: (obligation.ownerType as AssigneeType | null) ?? "team", ownerId: obligation.ownerId ?? "" };
}

function ownershipDecision(needsReview: boolean, recorded: boolean) {
  if (needsReview) return { triggerLabel: "Review and assign", submitLabel: "Confirm and assign", successMessage: "Obligation confirmed and assigned.", action: "confirm" as const };
  if (recorded) return { triggerLabel: "Update owner", submitLabel: "Save owner", successMessage: "Obligation owner updated.", action: "assign" as const };
  return { triggerLabel: "Record responsibility", submitLabel: "Save responsibility", successMessage: "Responsibility recorded in IO Spoke.", action: "confirm" as const };
}

export function ObligationActionSheet({ obligation, onUpdated, ownerSuggestions = [] }: Props) {
  const decisionAction = useAsyncAction();
  const ownership = obligationOwnership(obligation);
  const [open, setOpen] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const assignForm = useForm<AssignFormValues>({
    resolver: zodResolver(assignFormSchema),
    defaultValues: assignmentDefaults(obligation),
  });
  const dismissForm = useForm<DismissFormValues>({
    resolver: zodResolver(dismissFormSchema),
    defaultValues: { reason: "" },
  });

  useEffect(() => {
    assignForm.reset(assignmentDefaults(obligation));
  }, [obligation.id, obligation.ownerId, obligation.ownerType, ownership.recorded, assignForm]);

  const needsReview = obligation.requiresReview || obligation.state === "needs_review";
  const { triggerLabel, submitLabel, successMessage, action } = ownershipDecision(needsReview, ownership.recorded);

  const saveOwner = async (values: AssignFormValues) => decisionAction.run(async () => {
    setFormError(null);
    try {
      await applyObligationAction(obligation.id, { action, ownerType: values.ownerType, ownerId: values.ownerId });
      toast.success(successMessage);
      await onUpdated();
      setOpen(false);
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : "Unable to update this obligation.");
    }
  });

  const dismiss = async (values: DismissFormValues) => decisionAction.run(async () => {
    setFormError(null);
    try {
      await applyObligationAction(obligation.id, { action: "dismiss", reason: values.reason });
      toast.success("Obligation dismissed after review.");
      await onUpdated();
      setOpen(false);
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : "Unable to dismiss this obligation.");
    }
  });

  return (
    <>
      <Button variant={needsReview ? "default" : "outline"} onClick={() => setOpen(true)}>{triggerLabel}</Button>
      <Sheet open={open} onOpenChange={(next) => { if (decisionAction.pending) return; setOpen(next); if (!next) { setShowDismiss(false); setFormError(null); dismissForm.reset({ reason: "" }); } }}>
        <SheetContent side="right" className="h-full w-full overflow-y-auto [&>button]:h-11 [&>button]:w-11 sm:max-w-lg">
          <SheetHeader className="text-left">
            <SheetTitle>{needsReview ? "Review and assign obligation" : triggerLabel}</SheetTitle>
            <SheetDescription>
              {needsReview ? "Confirm that the evidence represents real customer-impacting work, then name who owns the next step." : "Keep accountability current without changing the connected source."}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 rounded-lg border bg-muted/30 p-4">
            <p className="font-medium">{obligation.title}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{obligation.impact}</p>
            <div className="mt-4 border-t pt-4"><ObligationOwnership obligation={obligation} /></div>
          </div>

          {!showDismiss ? (
            <form className="mt-6 space-y-5" onSubmit={assignForm.handleSubmit(saveOwner)} noValidate>
              <div className="space-y-2">
                <Label htmlFor={`owner-type-${obligation.id}`}>Owner type</Label>
                <Controller control={assignForm.control} name="ownerType" render={({ field }) => <Select value={field.value} onValueChange={field.onChange} disabled={decisionAction.pending}>
                  <SelectTrigger id={`owner-type-${obligation.id}`} onBlur={field.onBlur} ref={field.ref}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="team">Team</SelectItem><SelectItem value="role">Role</SelectItem><SelectItem value="human">Person</SelectItem><SelectItem value="agent">Automation agent</SelectItem></SelectContent>
                </Select>} />
              </div>
              <Controller control={assignForm.control} name="ownerId" render={({ field }) => <AccountableOwnerField id={`owner-id-${obligation.id}`} type={assignForm.watch("ownerType")} value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} disabled={decisionAction.pending} error={assignForm.formState.errors.ownerId?.message} suggestions={ownerSuggestions} />} />
              {formError ? <p className="text-sm text-destructive" role="alert">{formError}</p> : null}
              <Button type="submit" className="w-full" loading={decisionAction.pending} loadingLabel="Saving decision…">
                {submitLabel}
              </Button>
              {needsReview ? <Button type="button" className="w-full" variant="ghost" onClick={() => { setShowDismiss(true); setFormError(null); }} disabled={decisionAction.pending}>This is not an obligation</Button> : null}
            </form>
          ) : (
            <form className="mt-6 space-y-5" onSubmit={dismissForm.handleSubmit(dismiss)} noValidate>
              <div className="space-y-2">
                <Label htmlFor={`dismiss-reason-${obligation.id}`}>Why should this be dismissed?</Label>
                <Textarea id={`dismiss-reason-${obligation.id}`} {...dismissForm.register("reason")} placeholder="Explain what the evidence means instead." disabled={decisionAction.pending} aria-invalid={Boolean(dismissForm.formState.errors.reason)} aria-describedby={dismissForm.formState.errors.reason ? `dismiss-reason-${obligation.id}-error` : undefined} />
                {dismissForm.formState.errors.reason ? <p id={`dismiss-reason-${obligation.id}-error`} className="text-sm text-destructive" role="alert">{dismissForm.formState.errors.reason.message}</p> : null}
              </div>
              {formError ? <p className="text-sm text-destructive" role="alert">{formError}</p> : null}
              <Button type="submit" className="w-full" variant="destructive" loading={decisionAction.pending} loadingLabel="Saving decision…">
                Dismiss obligation
              </Button>
              <Button type="button" className="w-full" variant="outline" onClick={() => { setShowDismiss(false); setFormError(null); }} disabled={decisionAction.pending}>Back to assignment</Button>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
